import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { F1125SchAService } from '../../services/f1125-sch-a.service';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  generalConfigFromGlobal,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1125-sch-a',
    templateUrl: './f1125-sch-a.component.html',
    styleUrls: ['./f1125-sch-a.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1125SchAComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  // 🔴 STANDARD COMPONENT PROPERTIES (SuperPrompt V6)
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  activeTab = 1;
  
  // Input properties from AngularJS
  baseURLs: any = {};
  @Input('capture-custom-events') captureCustomEvents: any;

  // 🔴 STANDARD MODAL PROPERTIES
  modalTitle = "Cost of Goods Sold";
  loading = false;
  showFormLoader = false;
  crudLoading = false;
  dataLoaded = false; // Flag to prevent multiple API calls
  
  // 🔴 STANDARD ENTITY DATA PROPERTIES  
  entityList: any[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';
  
  // Form data properties from AngularJS
  scheduleFFormView: any = { rows: [] };
  formListOriginal: any = {};
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };
  mainData: any[] = [];
  isSaving = false;
  ISSUE_DESC = '';
  
  // Services and configurations
  globalService: any;
  filterParams: any = {};
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  rowData: any = {};
  
  // Tabs configuration
  tabs = [
    {id: 1, name: "Form Entry", active: true}
  ];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private f1125SchAService: F1125SchAService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.dataLoaded = false;
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.dataLoaded = false;
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 && 
          this.globalService && !this.dataLoaded) {
        
        // 🔴 CRITICAL: Call setupEntityData() first (SuperPrompt V6)
        this.setupEntityData();
        
        // Set modal title with scenario
        this.setModalTitle();
        
        // Set ISSUE_DESC
        this.ISSUE_DESC = this.rowData.ISSUE_DESC || '';
        
        // Load form data
        this.getModalData();
        
        this.dataLoaded = true;
      }
    } catch (e) {
      console.error('Error in component init:', e);
    }
  }

  // 🔴 CRITICAL: Standard setupEntityData method (SuperPrompt V6)
  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // 🔴 MANDATORY ORDER: Tax Year, LEID, LE Name (SuperPrompt V6)
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  private setModalTitle() {
    // Get scenario label similar to AngularJS - need to find the scenario label like in AngularJS
    let scenarioLabel = 'BASE'; // Default
    
    console.log('🔧 F1125SchA - Looking for scenario label');
    console.log('🔧 F1125SchA - globalService:', this.globalService);
    console.log('🔧 F1125SchA - filterParams:', this.filterParams);
    
    // Try multiple sources for scenario label
    if (this.filterParams && this.filterParams.scenario_label) {
      scenarioLabel = this.filterParams.scenario_label;
    } else if (this.globalService && this.globalService.workspaceFactory?.activeScreen?.filters?.filters) {
      const scenarioFilter = this.globalService.workspaceFactory.activeScreen.filters.filters.find((f: any) => f.param_name === 'scenario');
      if (scenarioFilter && scenarioFilter.selected) {
        scenarioLabel = scenarioFilter.selected.LABEL;
      }
    } else if (this.rowData.SCENARIO_LABEL) {
      scenarioLabel = this.rowData.SCENARIO_LABEL;
    }
    
    console.log('🔧 F1125SchA - Using scenario label:', scenarioLabel);
    
    // 🔴 CRITICAL: Add parentheses like in AngularJS - vm.title = 'Cost of Goods Sold' + ' ( Tax Year : ' + rowData.TAX_YEAR + ' - Scenario : ' + vm.scenarioLabel + ' )';
    this.modalTitle = `Cost of Goods Sold ( Tax Year : ${this.rowData.TAX_YEAR} - Scenario : ${scenarioLabel} )`;
  }

  getModalData() {
    this.showFormLoader = true;
    
    const params = {
      "action_id": 31286,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO_KEY,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY, 
      "schedule_name": "A"
    };

    console.log('🔧 F1125SchA - Loading data with params:', params);

    this.f1125SchAService.loadF1125SchAData(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('🔧 F1125SchA - Received data:', data);
        if (data && data.irsForm) {
          this.processFormData(data);
        } else {
          console.error('🔧 F1125SchA - Invalid response structure:', data);
          this.gtwSnackbar.openSnackBar('Invalid data structure received from server.', 'gtw-snackbar--red', true, 4);
        }
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('🔧 F1125SchA - Error loading form data:', error);
        this.showFormLoader = false;
        this.gtwSnackbar.openSnackBar('Error loading form data. Please try again.', 'gtw-snackbar--red', true, 4);
      }
    );
  }

  processFormData(data: any) {
    try {
      // 🔴 CRITICAL: Don't overwrite modalTitle - keep the one with Tax Year and Scenario
      // Original title is already set in setModalTitle() method

      let saveParamDtls = [];
      let tempDataObj: any = {};
      let tempDataObj2: any = {};
      this.scheduleFFormView = { rows: [] };

      for (let i = 0; i < data.irsForm.formItemList.length; i++) {
        saveParamDtls = [];
        tempDataObj = {};
        tempDataObj2 = {};
        
        saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
        tempDataObj.saveParams = saveParamDtls;                   
        tempDataObj2.saveParams = saveParamDtls;

        for (let j = 0; j < data.irsForm.formItemList[i].dataRowList[0].dataCellList.length; j++) {
          if (j == 0) {
            tempDataObj.num = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].name;
            tempDataObj2.num = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].name;
          } else if (j == 1) {
            tempDataObj.description = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].name;
            tempDataObj2.description = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].name;
          } else if (j == 2) {
            tempDataObj.lineAttrKeyA = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
            tempDataObj.attrib_name_a = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
            tempDataObj.attrib_order_a = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
            
            if (i == 17) {
              if (data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value == null) {
                tempDataObj.value1 = 'false';
              } else {
                tempDataObj.value1 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
              }
            } else {
              tempDataObj.value1 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
            }
          } else {
            tempDataObj2.lineAttrKeyB = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
            tempDataObj2.attrib_name_b = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
            tempDataObj2.attrib_order_b = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
            
            if (i == 17) {
              if (data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value == null) {
                tempDataObj2.value1 = 'false';
              } else {
                tempDataObj2.value1 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
              }
            } else {
              tempDataObj2.value1 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
            }
          }  
        }
        
        tempDataObj.line_order = saveParamDtls[8];
        tempDataObj2.line_order = saveParamDtls[8];
        this.scheduleFFormView.rows.push(tempDataObj);
        
        if (tempDataObj2?.attrib_name_b) {
          this.scheduleFFormView.rows.push(tempDataObj2);
        }
      }
      
      // Set entity data in form
      this.scheduleFFormView.rows[1].value1 = this.rowData.LE_NAME; 
      this.scheduleFFormView.rows[2].value1 = this.rowData.EIN;
      
      // Keep original copy for reset functionality
      this.formListOriginal = _.cloneDeep(this.scheduleFFormView);
      
    } catch (error) {
      console.error('Error processing form data:', error);
      this.gtwSnackbar.openSnackBar('Error processing form data.', 'gtw-snackbar--red', true, 4);
    }
  }

  // Business logic methods from AngularJS
  clickLine17() {
    if (this.scheduleFFormView.rows[17].value1 == 'false') {
      this.scheduleFFormView.rows[18].value1 = 0;
    }
  }
  
  computeValuesForEandF1() {
    if (this.scheduleFFormView.rows[19].value1 == 'true') {
      this.scheduleFFormView.rows[20].value1 = 'false';
    }
  }
  
  computeValuesForEandF2() {
    if (this.scheduleFFormView.rows[20].value1 == 'true') {
      this.scheduleFFormView.rows[19].value1 = 'false';
    }
  }
  
  computeValuesForEandF3() {
    if (this.scheduleFFormView.rows[21].value1 == 'true') {
      this.scheduleFFormView.rows[22].value1 = 'false';
    }
  }
  
  computeValuesForEandF4() {
    if (this.scheduleFFormView.rows[22].value1 == 'true') {
      this.scheduleFFormView.rows[21].value1 = 'false';
    }
  }

  // 🔴 STANDARD SAVE METHOD PATTERN (SuperPrompt V6)
  save() {
    this.isSaveClicked = true; // Disable button during save
    this.crudLoading = true;
    
    // Business logic before save
    if (this.scheduleFFormView.rows[14].value1 == 'false') {
      this.scheduleFFormView.rows[15].value1 = '';
    }
    
    this.isSaving = true;
    this.showFormLoader = true;
    
    // Build form data exactly like AngularJS
    let formRowDataA: any = {};
    let tempArray = [];
    
    for (let i = 0; i < this.scheduleFFormView.rows.length; i++) {
      // Sanitize numeric values before saving (especially for LIFO field)
      let attributeValue = this.scheduleFFormView.rows[i].value1;
      
      // Check if this is a numeric field that needs validation
      if (i === 15 || i === 18) { // Row 15 and 18 are the text input fields that might be LIFO
        if (attributeValue && typeof attributeValue === 'string' && attributeValue.trim() !== '') {
          const numericValue = parseFloat(attributeValue);
          if (isNaN(numericValue)) {
            console.warn(`Converting non-numeric LIFO value to 0: "${attributeValue}"`);
            attributeValue = '0';
          } else {
            attributeValue = numericValue.toString();
          }
        }
      }
      
      formRowDataA = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: this.scheduleFFormView.rows[i].saveParams[1],
        line_order: this.scheduleFFormView.rows[i].line_order,
        attrib_order: this.scheduleFFormView.rows[i].attrib_order_a || this.scheduleFFormView.rows[i].attrib_order_b,
        line_description: this.scheduleFFormView.rows[i].description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: this.scheduleFFormView.rows[i].saveParams[2],
        line_no: this.scheduleFFormView.rows[i].num,
        occurence: this.scheduleFFormView.rows[i].saveParams[7],
        line_attrib_key: this.scheduleFFormView.rows[i].lineAttrKeyA || this.scheduleFFormView.rows[i].lineAttrKeyB,
        Attribute_Name: this.scheduleFFormView.rows[i].attrib_name_a || this.scheduleFFormView.rows[i].attrib_name_b,
        Attribute_Value: attributeValue,
        trans_dtls_key: this.scheduleFFormView.rows[i].saveParams[3],
        trans_status: this.scheduleFFormView.rows[i].saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataA);
    }
    
    this.formData.editrows.push(tempArray);
    this.mainData = [];
    this.mainData.push(this.formData);

    console.log('🔧 F1125SchA - Saving data:', this.mainData);
    console.log('🔧 F1125SchA - filterParams:', this.filterParams);
    console.log('🔧 F1125SchA - rowData:', this.rowData);

    this.f1125SchAService.saveF1125SchAForm(
      this.GENERAL_CONFIG.base_url,
      this.mainData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        console.log('🔧 F1125SchA - Save response:', response);
        
        this.isSaveClicked = false; // Re-enable button
        this.crudLoading = false;
        this.isSaving = false;
        this.showFormLoader = false;
        
        if (response.callSuccess == "1" && response.errorMessage == "null") {
          this.gtwSnackbar.openSnackBar('Transactions saved successfully', 'gtw-snackbar--green', true, 4);
          
          // 🔴 CRITICAL: Emit 'refresh' string (NOT objects) - SuperPrompt V6
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbar.openSnackBar(response.errorMessage || 'Error saving data', 'gtw-snackbar--red', true, 4);
        }
      },
      (error: any) => {
        console.error('🔧 F1125SchA - Save error:', error);
        
        this.isSaveClicked = false; // Re-enable button on error
        this.crudLoading = false;
        this.isSaving = false;
        this.showFormLoader = false;
        
        this.gtwSnackbar.openSnackBar('Error saving data. Please try again.', 'gtw-snackbar--red', true, 4);
      }
    );
  }

  // 🔴 STANDARD CANCEL METHOD PATTERN (SuperPrompt V6)
  cancel() {
    // 🔴 CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  reset() {
    this.scheduleFFormView = _.cloneDeep(this.formListOriginal);
  }

  // Method to handle checkbox changes in the enhanced table
  onCheckboxChange(event: Event, rowIndex: number) {
    const target = event.target as HTMLInputElement;
    if (this.scheduleFFormView.rows[rowIndex]) {
      this.scheduleFFormView.rows[rowIndex].value1 = target.checked ? 'true' : 'false';
      
      // Special logic for 9a(iii) - clear text input when unchecked
      if (rowIndex === 14 && !target.checked && this.scheduleFFormView.rows[15]) {
        this.scheduleFFormView.rows[15].value1 = '';
      }
    }
  }

  // Method to handle radio button changes for Yes/No inputs
  onRadioChange(index: number, value: boolean, row: any) {
    row.value1 = value ? 'true' : 'false';
    // Trigger business logic based on row index if needed
    if (index === 17) {
      this.clickLine17();
    } else if (index === 19) {
      this.computeValuesForEandF1();
    } else if (index === 20) {
      this.computeValuesForEandF2();
    } else if (index === 21) {
      this.computeValuesForEandF3();
    } else if (index === 22) {
      this.computeValuesForEandF4();
    }
  }

  // Method for disabling other checkboxes (from AngularJS template)
  disableOtherH(lineIndex: number) {
    // This method was called in AngularJS but implementation wasn't provided
    // Based on context, it might clear text field when checkbox is unchecked
    if (this.scheduleFFormView.rows[lineIndex] && this.scheduleFFormView.rows[lineIndex].value1 === 'false') {
      this.scheduleFFormView.rows[lineIndex + 1].value1 = '';
    }
  }

  // Method for tab assignment (like F7004)
  assignTabNames(tabName: string) {
    this.currentTabName = tabName;
  }

  // BLOCK non-numeric characters on keypress (real-time blocking)
  allowOnlyNumeric(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(charCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (charCode === 65 && event.ctrlKey) || 
        (charCode === 67 && event.ctrlKey) || 
        (charCode === 86 && event.ctrlKey) ||
        (charCode === 88 && event.ctrlKey) ||
        // Allow: home, end, left, right
        (charCode >= 35 && charCode <= 39)) {
      return true;
    }
    
    const char = String.fromCharCode(charCode);
    
    // Allow only: numbers (0-9), minus (-), decimal point (.)
    if (!/[0-9\-\.]/.test(char)) {
      event.preventDefault();
      return false;
    }
    
    return true;
  }

  // Handle input event for immediate validation
  onNumericInput(event: any, row: any): void {
    const input = event.target;
    const value = input.value;
    
    // Remove any non-numeric characters except minus and decimal
    const sanitized = value.replace(/[^0-9\-\.]/g, '');
    
    // If value changed after sanitization, update it
    if (sanitized !== value) {
      row.value1 = sanitized;
      // Update the input field immediately
      input.value = sanitized;
    }
  }

  // Handle paste event to block non-numeric content
  onPaste(event: ClipboardEvent, row: any): void {
    event.preventDefault();
    
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      // Only allow numeric characters, minus and decimal point
      const sanitized = pastedText.replace(/[^0-9\-\.]/g, '');
      
      if (sanitized !== '') {
        row.value1 = sanitized;
        console.log(`Pasted and sanitized: "${pastedText}" -> "${sanitized}"`);
      }
    }
  }

  // Sanitize numeric values on blur to ensure valid numbers
  sanitizeNumericValue(row: any): void {
    if (row && row.value1 !== undefined && row.value1 !== null) {
      const value = row.value1.toString().trim();
      
      if (value === '' || value === '-') {
        row.value1 = '0';
        return;
      }

      // Parse as float and check if valid
      const numericValue = parseFloat(value);
      
      if (isNaN(numericValue)) {
        // If not a valid number, set to 0
        row.value1 = '0';
        console.warn(`Invalid number entered in LIFO field. Value reset to 0. Original value: "${value}"`);
      } else {
        // Ensure we store as string (backend expects string)
        row.value1 = numericValue.toString();
        console.log(`LIFO field sanitized: "${value}" -> "${row.value1}"`);
      }
    }
  }
}
